// Scene-mode SH renderer: @mkkellogg/gaussian-splats-3d's DropInViewer mounted inside the
// R3F scene. The packed-.splat path (drei <Splat>) stores one flat color per gaussian, so
// speculars — the whole point of scene captures of shiny equipment — render flat there.
// This viewer evaluates spherical harmonics up to degree 2 in-shader from the full-SH .ply.
//
// Integration rules learned on the harness (do not "simplify" these away):
// - The DropInViewer self-updates via an onBeforeRender hook when the containing scene
//   renders — NEVER call its update() manually (it assumes drop-in render context).
// - sharedMemoryForWorkers must stay false: projects.cedarpost.com serves no COOP/COEP
//   headers, so SharedArrayBuffer is unavailable.
// - The library is dynamically imported so object-mode viewers never pay its bundle cost.
// - It renders raw PLY coordinates — no [Math.PI,0,0] flip group (that wrapper exists only
//   to cancel drei's packed-.splat center remapping).
import { useEffect } from "react";
import { useThree } from "@react-three/fiber";

interface Props {
  url: string;
  /** 0..1 download/parse progress from the library. */
  onProgress?: (fraction: number) => void;
  /** The splats are in the scene and rendering. */
  onReady?: () => void;
  /** Import or load failed — caller should fall back to the SH0 path. */
  onError?: (message: string) => void;
}

export function ShSplatScene({ url, onProgress, onReady, onError }: Props) {
  const scene = useThree((s) => s.scene);

  useEffect(() => {
    let disposed = false;
    let mounted: import("@mkkellogg/gaussian-splats-3d").DropInViewer | null = null;
    (async () => {
      try {
        const GS = await import("@mkkellogg/gaussian-splats-3d");
        if (disposed) return;
        const viewer = new GS.DropInViewer({
          sphericalHarmonicsDegree: 2,
          sharedMemoryForWorkers: false,
        });
        await viewer.addSplatScene(url, {
          format: GS.SceneFormat.Ply,
          showLoadingUI: false,
          progressiveLoad: false,
          onProgress: (percent) => onProgress?.(Math.min(1, percent / 100)),
        });
        if (disposed) {
          viewer.dispose().catch(() => {});
          return;
        }
        mounted = viewer;
        scene.add(viewer);
        onReady?.();
      } catch (e) {
        if (!disposed) onError?.(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      disposed = true;
      if (mounted) {
        scene.remove(mounted);
        mounted.dispose().catch(() => {});
      }
    };
    // url is the only reactive input; the callbacks are informational.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, scene]);

  return null;
}
